import { breakpoints } from '@galapagos/design-system/breakpoints';
import { colors } from '@galapagos/design-system/colors';
import { radius } from '@galapagos/design-system/radius';
import { sizing } from '@galapagos/design-system/sizing';
import { spacing } from '@galapagos/design-system/spacing';
import { typography } from '@galapagos/design-system/typography';

type TypographyToken = keyof typeof typography;

export const SIGNUP_CHROME_STYLE_ID = 'signup-chrome-style';

/** The header's measured height, set on the root by the header itself. */
export const SIGNUP_HEADER_HEIGHT_VAR = '--signup-header-height';

function font(token: TypographyToken): string {
  const t = typography[token];
  return `font-family: ${t.fontFamily}; font-weight: ${t.fontWeight}; font-size: ${t.fontSize}px; line-height: ${t.lineHeight}px; letter-spacing: ${t.letterSpacing}px;`;
}

/**
 * The v8 site chrome (Figma page v8, section 4) as one stylesheet, because
 * inline styles cannot hold media or hover queries. Phone layout is the
 * default; the desktop layout sits behind the page-column breakpoint. Every
 * value comes from a design-system token. Figma greys are mapped by value, not
 * by Figma variable name. The footer is white with a hairline top divider:
 * stacked on phones, one row on desktop with decorative middle dots that
 * screen readers skip. Hover rules only apply to devices that can hover, so a
 * tap never leaves a link stuck in its hover look. The main area clips sideways overflow so the
 * phone action bar can run to the screen edges without a scrollbar ever
 * turning it into a sideways scroll; `clip` keeps sticky positioning working.
 * Inside a step, the step's own row gap is the space under the title. On
 * desktop the header is a three-column grid, so the progress bar sits in the
 * middle of the header whatever the logo and switch widths, and the switch
 * keeps its column on pages with no bar. On phones the header sticks to the
 * top so the progress bar stays in view on long steps; the Continue bar sticks
 * to the bottom, so the two never meet. The page leaves the header's height
 * clear at the top whenever it scrolls something into view, so a jumped-to
 * section or a focused field never lands under it. The step heading hides its focus
 * outline here; the shell draws the design-system ring on it only for a member
 * using the keyboard.
 */
export function signupChromeCss(): string {
  const bp = breakpoints.column;
  return `
html:has(.signup-chrome) { scroll-padding-top: var(${SIGNUP_HEADER_HEIGHT_VAR}, 0); }
.signup-chrome { min-height: 100dvh; display: flex; flex-direction: column; background: ${colors.background.container}; }
.signup-chrome__main { flex: 1; display: flex; flex-direction: column; overflow-x: clip; }
.signup-header { position: sticky; top: 0; z-index: 1; background: ${colors.background.container}; border-bottom: ${sizing.stroke.hairline}px solid ${colors.stroke.divider}; display: flex; flex-direction: column; padding-bottom: ${spacing.sm}px; }
.signup-header__bar { display: flex; align-items: center; justify-content: space-between; min-height: ${sizing.touchTarget.minimum + spacing.s}px; padding: 0 ${spacing.m}px 0 ${spacing.lg}px; }
.signup-header__logo { display: block; height: ${sizing.icon.sm}px; width: auto; }
.signup-header__progress { padding: 0 ${spacing.lg}px; }
.signup-header__progress:empty { display: none; }
.signup-language-switch { display: flex; align-items: center; gap: ${spacing.xs}px; }
.signup-language-switch__option { appearance: none; background: none; border: none; margin: 0; cursor: pointer; min-width: ${sizing.controlButton.md}px; min-height: ${sizing.controlButton.md}px; padding: ${spacing.s}px ${spacing.sm}px; border-radius: ${radius.m}px; color: ${colors.text.secondary}; text-decoration: underline; ${font('c1Medium')} }
.signup-language-switch__option[aria-current='true'] { color: ${colors.text.primary}; text-decoration: none; font-weight: ${typography.b3Bold.fontWeight}; }
.signup-language-switch__divider { color: ${colors.text.tertiary}; ${font('c1Medium')} }
.signup-footer { background: ${colors.background.container}; border-top: ${sizing.stroke.hairline}px solid ${colors.stroke.divider}; padding: ${spacing.lg}px; display: flex; flex-direction: column; gap: ${spacing.m}px; color: ${colors.text.primary}; }
.signup-footer__help { display: flex; flex-wrap: wrap; align-items: center; gap: ${spacing.s}px ${spacing.lg}px; }
.signup-footer__help-title { color: ${colors.text.primary}; ${font('b2Bold')} }
.signup-footer__bottom { display: flex; flex-direction: column; gap: ${spacing.s}px; }
.signup-footer__legal { display: flex; flex-wrap: wrap; gap: ${spacing.s}px ${spacing.m}px; margin: 0; padding: 0; list-style: none; }
.signup-footer__link { display: inline-flex; align-items: center; gap: ${spacing.xs}px; color: ${colors.text.secondary}; text-decoration: underline; border-radius: ${radius.sm}px; ${font('b3Regular')} }
.signup-footer__link--quiet { gap: ${spacing.s}px; text-decoration: none; ${font('b2Regular')} }
.signup-footer__copyright { margin: 0; color: ${colors.text.secondary}; ${font('b3Regular')} }
.signup-visually-hidden { position: absolute; width: ${sizing.stroke.hairline}px; height: ${sizing.stroke.hairline}px; margin: -${sizing.stroke.hairline}px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.signup-page-title { display: flex; flex-direction: column; gap: ${spacing.s}px; margin-bottom: ${spacing.m}px; }
.signup-page-title__heading:focus { outline: none; }
.signup-page-title__heading { margin: 0; color: ${colors.text.primary}; text-wrap: balance; ${font('h1Bold28')} }
.signup-page-title__description { margin: 0; color: ${colors.text.secondary}; ${font('b2Regular')} }
.signup-step > .signup-page-title { margin-bottom: 0; }
@media (hover: hover) {
  .signup-footer__link:hover { color: ${colors.text.primary}; text-decoration-thickness: ${sizing.stroke.strong}px; }
  .signup-footer__link--quiet:hover { text-decoration: underline; }
  .signup-language-switch__option:hover { color: ${colors.text.primary}; }
}
@media (min-width: ${bp}px) {
  html:has(.signup-chrome) { scroll-padding-top: 0; }
  .signup-header { position: static; display: grid; grid-template-columns: 1fr ${sizing.column.progress}px 1fr; align-items: center; min-height: ${sizing.controlButton.lg}px; padding: ${spacing.sm}px ${spacing.xxl}px; box-sizing: border-box; }
  .signup-header__bar { display: contents; }
  .signup-header__logo { grid-row: 1; grid-column: 1; justify-self: start; height: auto; width: ${sizing.avatar.xl}px; }
  .signup-header__progress { grid-row: 1; grid-column: 2; padding: 0; }
  .signup-language-switch { grid-row: 1; grid-column: 3; justify-self: end; }
  .signup-footer { flex-direction: row; flex-wrap: wrap; justify-content: space-between; align-items: center; padding: ${spacing.m}px ${spacing.xxl}px; }
  .signup-footer__bottom { flex-direction: row; flex-wrap: wrap; align-items: center; gap: ${spacing.s}px; }
  .signup-footer__legal { gap: ${spacing.s}px; }
  .signup-footer__legal li + li::before, .signup-footer__copyright::before { content: '·' / ''; margin-right: ${spacing.s}px; color: ${colors.text.tertiary}; }
  .signup-chrome__main { padding-top: ${spacing.lg}px; }
  .signup-page-title { margin-bottom: ${spacing.lg}px; }
  .signup-step > .signup-page-title { margin-bottom: ${spacing.s}px; }
  .signup-page-title__heading { ${font('h1Bold')} }
}
`;
}

/** Injects the chrome stylesheet once per document. No-op during prerender. */
export function ensureSignupChromeStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(SIGNUP_CHROME_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = SIGNUP_CHROME_STYLE_ID;
  style.textContent = signupChromeCss();
  document.head.appendChild(style);
}
